import { Device } from '@capacitor/device';
import type { Mixpanel } from 'mixpanel-browser';
import mixpanel from 'mixpanel-browser';
import { useEffect } from 'react';
import type { JSX } from 'react';

import { getConfig } from 'pkg/config/app';

import type * as models from 'pkg/api/models';
import { AccountFlags } from 'pkg/api/models/account';
import { hasFlag } from 'pkg/api/models/account/guards';
import { legacyRoleString } from 'pkg/api/models/membership/utils';
import { isCypress, isDev, isDevServer } from 'pkg/flags';
import useComponentDidMount from 'pkg/hooks/useComponentDidMount';
import {
	useCurrentAccount,
	useCurrentMembership,
	useCurrentMemberships,
	useCurrentOrganization,
} from 'pkg/identity';
import { isAndroid, isApp, isIOS } from 'pkg/platform';
import { useCurrentRoute } from 'pkg/router';

import {
	hasRememberedOptOut,
	rememberOptOut,
} from 'components/analytics/opt_out';
import type { AnalyticsSession } from 'components/analytics/session';
import {
	clearStoredSession,
	readStoredSession,
	resolveSession,
	storeSession,
} from 'components/analytics/session';

/**
 * Every event the app sends to Mixpanel. Billing is per event, so an event is
 * added only when someone will read it.
 *
 * Each member documents what it records and why we track it; `@why` is the
 * reason someone reads it, `@props` the properties sent through a wrapper
 * that the overview can't read from the call. contributing/analytics-events.md
 * is generated from this enum (`bun --filter app generate:analytics-events`)
 * and a test fails when it is out of date.
 */
export enum Event {
	/**
	 * Defined for ad impressions but not sent anywhere; the first-party ad
	 * server counts impressions itself.
	 */
	AdView = 'adview',
	/**
	 * A CSV export was downloaded. `endpoint` is the export's API path with
	 * ids replaced by `_`.
	 */
	Export = 'export',
	/** An admin merged two users into one. */
	UserMerge = 'user_merge',
	/** The group switcher was used to open the club lobby. */
	GroupSwitcherClubLobby = 'group_switcher_club_lobby',
	/** The group switcher was used to turn on family mode. */
	GroupSwitcherFamilyMode = 'group_switcher_family_mode',
	/** The group switcher was used to change to another group. */
	GroupSwitcherGroupChange = 'group_switcher_group_change',
	/**
	 * The app blocked navigation to ask for required information that is
	 * missing, for a ward or for a Swedish club's admin or staff.
	 */
	MissingInformationView = 'missing_information_view',
	/** Defined for the support chat but not sent anywhere. */
	SupportChatActivated = 'support_chat_activated',
	/** An item in the dashboard's quick-create menu was picked. */
	DashboardQuickActionClick = 'dashboard_quick_action_click',
	/** An order was marked as paid, uncollectible or void. */
	OrderMarkAs = 'order_mark_as',
	/** A subscription was cancelled. */
	SubscriptionCancel = 'subscription_cancel',
	/** A training session was connected to a calendar event. */
	ConnectSessionToEvent = 'connect_session_to_event',
	/**
	 * A registration form was submitted. Timed from when the form opened, so
	 * Mixpanel's duration is how long filling it in took.
	 */
	TimedFormSubmissionSubmit = 'timed_form_submission_submit',
	/** A lineup template was imported into a game lineup. */
	GameLineupImport = 'game_lineup_import',
	/** A training collection was shared with other organizations. */
	TrainingCollectionShare = 'training_collection_share',
	/** An exercise was printed. */
	TrainingExercisePrint = 'training_exercise_print',
	/** A printable page was opened. `route_pattern` names the page without its ids. */
	PrintablePageView = 'printable_page_view',
	/** A quick action modal in the large-screen header was opened. */
	QuickActionModal = 'quick_action_modal',
	/** An "App Connect" folder was opened. */
	GroupLinkFolderClick = 'group_link_folder_click',
	/** A link inside an "App Connect" folder was opened. */
	GroupLinkClick = 'group_link_click',
	/** A message from the AI assistant was rated. */
	AIMessageRated = 'ai_message_rated',
	/** The app rating sheet was shown to the user. */
	AppRatingPromptShown = 'app_rating_prompt_shown',
	/**
	 * The user picked a rating. `rating` carries the 1-5 score and `outcome`
	 * whether we sent them to the store ('store') or offered the hosted
	 * feedback form ('feedback').
	 */
	AppRatingPromptAnswered = 'app_rating_prompt_answered',
	/** The user closed the app rating sheet without picking a rating. */
	AppRatingPromptDismissed = 'app_rating_prompt_dismissed',
	/**
	 * A schedule import started from a file or pasted text. The event import
	 * events follow the flow from the file to what the club did with what
	 * came back; counts and codes only, no team names, row text or file names.
	 *
	 * @why The import funnel: where clubs drop off between the file and an
	 * imported schedule, and why.
	 */
	EventImportStarted = 'event_import_started',
	/**
	 * The import finished reading the schedule. Timed from the start, so
	 * Mixpanel's duration is how long reading took.
	 *
	 * @why The import funnel: where clubs drop off between the file and an
	 * imported schedule, and why.
	 */
	EventImportReadCompleted = 'event_import_read_completed',
	/**
	 * The club answered a problem the import found, by action, reply or instruction.
	 *
	 * @why The import funnel: where clubs drop off between the file and an
	 * imported schedule, and why.
	 */
	EventImportCauseAnswered = 'event_import_cause_answered',
	/**
	 * The club confirmed the import preview and started the import.
	 *
	 * @why The import funnel: where clubs drop off between the file and an
	 * imported schedule, and why.
	 */
	EventImportConfirmed = 'event_import_confirmed',
	/**
	 * An import was undone, from the flow, the import or the listing.
	 *
	 * @why The import funnel: where clubs drop off between the file and an
	 * imported schedule, and why.
	 */
	EventImportUndone = 'event_import_undone',
	/**
	 * The rows an import rejected were retried as a new import.
	 *
	 * @why The import funnel: where clubs drop off between the file and an
	 * imported schedule, and why.
	 */
	EventImportRetryRejected = 'event_import_retry_rejected',
	/**
	 * Reading the schedule never finished and the flow stopped polling.
	 *
	 * @why The import funnel: where clubs drop off between the file and an
	 * imported schedule, and why.
	 */
	EventImportReadStalled = 'event_import_read_stalled',
	/**
	 * The import failed while reading, confirming or running.
	 *
	 * @why The import funnel: where clubs drop off between the file and an
	 * imported schedule, and why.
	 */
	EventImportFailed = 'event_import_failed',
	/**
	 * The import finished, with how many events were created, updated or rejected.
	 *
	 * @why The import funnel: where clubs drop off between the file and an
	 * imported schedule, and why.
	 */
	EventImportCompleted = 'event_import_completed',
	/** The AI assistant created a training session. */
	AITrainingSessionCreated = 'ai_training_session_created',
	/**
	 * A child was put on a program's waitlist. The waitlist events follow an
	 * entry from joining to booking.
	 *
	 * @props organization_id, form_id, event_group_ids, user_ids, child_count
	 * @why The waitlist's success metrics (REG-1286): entries per week,
	 * Invited → Booked conversion and median days from invite to booking,
	 * which the stored data can't give.
	 */
	WaitlistJoined = 'waitlist_joined',
	/**
	 * A parent took a child off a program's waitlist.
	 *
	 * @props organization_id, form_id, event_group_ids, user_ids, child_count
	 * @why The waitlist's success metrics (REG-1286): entries per week,
	 * Invited → Booked conversion and median days from invite to booking,
	 * which the stored data can't give.
	 */
	WaitlistLeft = 'waitlist_left',
	/**
	 * An admin invited waitlisted children to book.
	 *
	 * @props organization_id, form_id, event_group_ids, user_ids, child_count
	 * @why The waitlist's success metrics (REG-1286): entries per week,
	 * Invited → Booked conversion and median days from invite to booking,
	 * which the stored data can't give.
	 */
	WaitlistInvited = 'waitlist_invited',
	/**
	 * An admin revoked a waitlist invite.
	 *
	 * @props organization_id, form_id, event_group_ids, user_ids, child_count
	 * @why The waitlist's success metrics (REG-1286): entries per week,
	 * Invited → Booked conversion and median days from invite to booking,
	 * which the stored data can't give.
	 */
	WaitlistInviteRevoked = 'waitlist_invite_revoked',
	/**
	 * An admin set a waitlist entry as declined.
	 *
	 * @props organization_id, form_id, event_group_ids, user_ids, child_count
	 * @why The waitlist's success metrics (REG-1286): entries per week,
	 * Invited → Booked conversion and median days from invite to booking,
	 * which the stored data can't give.
	 */
	WaitlistSetAsDeclined = 'waitlist_set_as_declined',
	/**
	 * An admin removed an entry from the waitlist.
	 *
	 * @props organization_id, form_id, event_group_ids, user_ids, child_count
	 * @why The waitlist's success metrics (REG-1286): entries per week,
	 * Invited → Booked conversion and median days from invite to booking,
	 * which the stored data can't give.
	 */
	WaitlistRemoved = 'waitlist_removed',
	/**
	 * A parent declined a waitlist invite.
	 *
	 * @props organization_id, form_id, event_group_ids, user_ids, child_count
	 * @why The waitlist's success metrics (REG-1286): entries per week,
	 * Invited → Booked conversion and median days from invite to booking,
	 * which the stored data can't give.
	 */
	WaitlistInviteDeclined = 'waitlist_invite_declined',
	/**
	 * A parent booked the spot a waitlist invite held.
	 *
	 * @props organization_id, form_id, event_group_ids, user_ids, child_count, paid
	 * @why The waitlist's success metrics (REG-1286): entries per week,
	 * Invited → Booked conversion and median days from invite to booking,
	 * which the stored data can't give.
	 */
	WaitlistInviteBooked = 'waitlist_invite_booked',
	/**
	 * An admin created subscriptions for one or more members in one batch.
	 * One event per batch; `country` is the first customer's billing country.
	 *
	 * @why How clubs adopt recurring billing, by country and currency.
	 */
	SubscriptionCreated = 'subscription_created',
	/**
	 * An admin created orders for one or more members in one batch. One event
	 * per batch; orders from registrations and subscription periods are not
	 * counted.
	 *
	 * @why How clubs use invoicing, by country and currency.
	 */
	OrderCreated = 'order_created',
	/**
	 * A registration form was created, from any of the create flows.
	 * `form_id` is the form's guid.
	 *
	 * @why The first step of the registration funnel: which clubs set up
	 * registrations.
	 */
	RegistrationFormCreated = 'registration_form_created',
	/**
	 * A registration form's landing page was opened, signed in or as a
	 * guest. `form_id` is the form's guid.
	 *
	 * @why The registration funnel's reach: how many see a form, and how many
	 * of them go on to start it.
	 * @props form_id, organization_id
	 */
	RegistrationFormViewed = 'registration_form_viewed',
	/**
	 * The first interaction with a registration form once its fields have
	 * loaded: typing in or changing a field, or clicking a button, link or
	 * choice card (event group, package, date, registrant). A click on empty
	 * space doesn't count. `form_id` is the form's guid.
	 *
	 * @why Separates started registrations from ones only viewed, so drop-off
	 * inside the form can be told from drop-off at the landing page.
	 * @props form_id, organization_id
	 */
	RegistrationStarted = 'registration_started',
	/**
	 * A single calendar event was created, for the team in `group_id`.
	 * `sends_invites` is whether the players were invited through 360Player.
	 *
	 * @why Adoption of the calendar per team. Customer managers see a team as
	 * having switched to 360Player once it invites players to games here
	 * rather than in an old tool.
	 * @props group_id, event_type, sends_invites
	 */
	CalendarEventCreated = 'calendar_event_created',
	/**
	 * A recurring event series was created.
	 *
	 * @why Adoption of recurring planning, next to single events.
	 * @props group_id, event_type, sends_invites
	 */
	EventSeriesCreated = 'event_series_created',
	/**
	 * A new chat or channel was created. Opening a personal chat that already
	 * exists does not count.
	 *
	 * @why Adoption of chat as the club's communication channel.
	 * @props group_id
	 */
	ChatConversationCreated = 'chat_conversation_created',
	/**
	 * An exercise was created in the training library.
	 *
	 * @why Adoption of the training library.
	 */
	ExerciseCreated = 'exercise_created',
	/**
	 * A drawing was saved on an exercise, one event per drawing.
	 *
	 * @why How much of the training library uses the drawing tool.
	 */
	ExerciseDrawingCreated = 'exercise_drawing_created',
	/**
	 * A video was added to the video library, by link (YouTube, Vimeo, raw),
	 * from Veo or from Wingfield. There is no file upload.
	 *
	 * @why Adoption of the video library.
	 * @props group_id
	 */
	VideoUploaded = 'video_uploaded',
	/**
	 * A video playlist was created.
	 *
	 * @why How video is put to use beyond single clips.
	 * @props group_id
	 */
	VideoPlaylistCreated = 'video_playlist_created',
	/**
	 * A performance review (player dialogue) was saved.
	 *
	 * @why Adoption of player development features.
	 * @props group_id
	 */
	PerformanceReviewCreated = 'performance_review_created',
	/**
	 * A page in the development section was opened. `page` is the sub-page:
	 * video-library, training-library, assessments, lineups,
	 * performance-reviews, practice-planner or evaluations.
	 *
	 * @why Which development tools are used, now that page views are gone.
	 * @props group_id
	 */
	DevelopmentPageViewed = 'development_page_viewed',
	/**
	 * The video analysis view was opened for a video.
	 *
	 * @why Adoption of video analysis, the tool coaches tag clips in.
	 * @props group_id
	 */
	VideoAnalyzed = 'video_analyzed',
	/**
	 * A viewing of a video ended: by leaving the page, hiding the app or
	 * moving on to another video. Pauses don't end a viewing, and seeking
	 * doesn't count as watched. `duration_seconds` is the time actually
	 * played.
	 *
	 * @why How much video is watched, not just added.
	 * @props video_id, duration_seconds, group_id
	 */
	VideoWatched = 'video_watched',
	/**
	 * A session started: the first activity after 30 idle minutes, shared
	 * across tabs. Every event carries the session_id super property.
	 *
	 * @why Sessions, active users and retention, which page views used to
	 * give. Mixpanel counts sessions by session_id.
	 */
	SessionStarted = 'session_started',
}

// Someone in the logged-out shell, such as a public registration form.
const GuestRole = 'guest';

// The club lobby takes the highest of these the user holds in the club.
const lobbyRoleRank = ['player', 'parent', 'staff', 'admin', '360admin'];

/**
 * The role value the analytics spec defines: admin, staff, parent or player.
 * Owners count as admins. 360 admins keep their own value so internal
 * accounts never mix with club admins. The club lobby says nothing about who
 * the user is, so it takes the highest role among their memberships in the
 * club. An unresolvable role gives ''.
 */
export function trackingRole(
	membership: models.membership.Membership,
	clubMemberships: models.membership.Membership[] = []
): string {
	if (!membership?.role) {
		return '';
	}

	// "All my children" is a parent looking at several wards at once; its
	// synthetic membership carries the player role.
	if (membership.isFamilyMembership) {
		return 'parent';
	}

	if (membership.isOrganizationMembership) {
		return clubMemberships
			.filter((m) => !m.isOrganizationMembership)
			.map((m) => trackingRole(m))
			.reduce(
				(highest, role) =>
					lobbyRoleRank.indexOf(role) > lobbyRoleRank.indexOf(highest)
						? role
						: highest,
				''
			);
	}

	const role = legacyRoleString(membership);

	switch (role) {
		case 'user':
			return 'player';
		case 'owner':
			return 'admin';
		case 'unknown':
			return '';
		default:
			return role;
	}
}

/**
 * The team an event is about, overriding the group_id super property (the
 * user's current group) where the two differ, such as an event created for
 * another team. Mixpanel's group analytics reads group_id.
 */
export function trackedGroup(groupId?: number | null): {
	group_id?: string;
} {
	if (!groupId) {
		return {};
	}

	return { group_id: String(groupId) };
}

/**
 * The club an event is about, overriding the organization_id super property
 * (the user's current club), which a guest doesn't have and a parent
 * registering at another club has wrong. Registration funnels are the club's
 * figures, not the user's.
 */
export function trackedOrganization(organizationId?: number | null): {
	organization_id?: string;
} {
	if (!organizationId) {
		return {};
	}

	return { organization_id: String(organizationId) };
}

// Staging and local development send to "App Analytics Stage", production
// to "App Analytics Prod". "App Analytics (Legacy)" keeps the history from
// before the event-based tracking (PLA-767). The API's pkg/analytics uses the
// same two.
const token = () =>
	isDev()
		? 'f4eeffc72742c340cd551d1ee7221547'
		: '830ab70ab5b6e6cf655d0694de35ff08';

let customEventQueue: {
	event: Event;
	props?: { [key: string]: unknown };
}[] = [];

let client: Mixpanel;

// Set once a signed-in account has opted out. Events are dropped rather than
// queued, so nothing tracked while opted out reaches a client created later.
let optedOut = false;

const shouldNotTrack = () => !client || isCypress();

let initializing: Promise<void>;

// The device's super properties, kept so they can be put back after a reset.
let baseProperties: { [key: string]: unknown } = {};

// Creates the client once per page load. The signed-in shell identifies it
// afterwards; the public shell leaves it anonymous, and Mixpanel links the
// anonymous id to the account on the first identify after a login.
const initClient = (): Promise<void> => {
	if (!initializing) {
		initializing = createClient();
	}

	return initializing;
};

const createClient = async () => {
	// Assigned to client only once its super properties are registered, so no
	// event is sent without them; until then events wait in the queue.
	const newClient = mixpanel.init(
		token(),
		{
			api_host: 'https://api-eu.mixpanel.com',
			debug: isDevServer(),
			ignore_dnt: isDevServer(),
			persistence: 'cookie',
			// Billing is per event: only the events in Event are sent.
			autocapture: false,
			track_pageview: false,
		},
		'webtracker'
	);

	let os = 'web';
	if (isIOS()) {
		os = 'ios';
	} else if (isAndroid()) {
		os = 'android';
	}

	const osVersion = await readOsVersion();

	// disable current_url and current_url_search to avoid sending PII
	baseProperties = {
		$current_url: '',
		current_url_search: '',
		environment: isDev() ? 'staging' : 'production',
		app_version: getConfig().version,
		os_version: osVersion,
		os,
	};
	newClient.register(baseProperties);

	client = newClient;
};

// The app asks the device: its web view's user agent has no Version/, so
// parsing it gave nothing on iOS. Browsers report it through
// userAgentData where they support it, and Safari through Version/.
export async function readOsVersion(): Promise<string | undefined> {
	if (isApp()) {
		try {
			return (await Device.getInfo()).osVersion || undefined;
		} catch {
			return undefined;
		}
	}

	if (navigator.userAgentData) {
		const ua = await navigator.userAgentData.getHighEntropyValues([
			'platformVersion',
		]);

		return ua.platformVersion || undefined;
	}

	return navigator.userAgent.match(/(iPhone|iPad);.+?Version\/([\d\.]+)/)?.[2];
}

let currentSession: AnalyticsSession = null;

// Whether the client has been identified with a signed-in account.
let identified = false;
let lastActivityAt = 0;

// Navigation, taps and keys count as activity without sending anything, so a
// session only ends after 30 minutes of nothing at all.
const ACTIVITY_THROTTLE_MS = 10 * 1000;

// Records activity, and starts a new session when the last one has gone
// idle. Only session_started is sent; the activity itself stays local.
const touchSession = () => {
	if (!client || optedOut || isCypress()) {
		return;
	}

	const now = Date.now();
	const { session, isNew } = resolveSession(
		readStoredSession() ?? currentSession,
		now,
		!identified
	);

	lastActivityAt = now;
	storeSession(session);

	if (session.id !== currentSession?.id) {
		client.register({ session_id: session.id });
	}

	currentSession = session;

	if (isNew) {
		client.track(Event.SessionStarted);
	}
};

const handleActivity = () => {
	if (Date.now() - lastActivityAt < ACTIVITY_THROTTLE_MS) {
		return;
	}

	touchSession();
};

const handleVisibilityChange = () => {
	if (document.visibilityState === 'visible') {
		touchSession();
	}
};

let listeningForActivity = false;

const startSession = () => {
	touchSession();

	if (listeningForActivity) {
		return;
	}

	listeningForActivity = true;

	['pointerdown', 'keydown'].forEach((type) =>
		document.addEventListener(type, handleActivity, {
			capture: true,
			passive: true,
		})
	);
	document.addEventListener('visibilitychange', handleVisibilityChange);
};

// track any events that was tracked before the client was initialized
const flushQueue = () => {
	customEventQueue.forEach((ev) => {
		client.track(ev.event, ev.props);
	});
	customEventQueue = [];
};

export const trackEvent = (
	event: Event,
	props?: { [key: string]: unknown }
) => {
	if (isCypress() || optedOut) {
		return;
	}

	if (props && props['endpoint'] && typeof props['endpoint'] === 'string') {
		// replace ids with _ when tracking "endpoint"
		props['endpoint'] = (props['endpoint'] as string)
			.split('/')
			.map((part) => {
				return /^\d+$/.test(part) ? '_' : part;
			})
			.join('/');
	}

	if (!client) {
		customEventQueue.push({ event, props });
		return;
	}

	touchSession();
	client.track(event, props);
};

export const startTimedTrackEvent = (event: Event) => {
	if (shouldNotTrack()) {
		return;
	}

	client.time_event(event);
};

export const stopTimedTrackEvent = (
	event: Event,
	props?: { [key: string]: unknown }
) => {
	if (shouldNotTrack()) {
		return;
	}

	client.track(event, props);
};

export const resetClient = () => {
	if (!client) {
		return;
	}

	client.reset();
	client.unregister(token());

	// reset() wipes every super property, but the client lives on for the
	// rest of the page load (logout doesn't reload), so the device's own are
	// put back. The next person starts a session of their own.
	client.register(baseProperties);
	currentSession = null;
	identified = false;
	clearStoredSession();
};

function Analytics(): JSX.Element {
	const account = useCurrentAccount();
	const membership = useCurrentMembership();
	const memberships = useCurrentMemberships();
	const org = useCurrentOrganization();

	const setMembership = (membership: models.membership.Membership) => {
		const role = trackingRole(membership, memberships);

		// useCurrentMembership returns an empty object while no membership is
		// resolvable — registering an undefined role would wipe the persisted
		// super property, so keep the last known role. The group is cleared
		// rather than kept: unlike a role, a stale group id misattributes
		// events to a team the user may no longer be in.
		if (!role) {
			client.register({ group_id: null });
			return;
		}

		// Family mode is club-wide, not one team.
		let groupId: string = null;
		if (!membership.isOrganizationMembership) {
			groupId = String(membership.groupId);
		}

		client.register({
			role,
			group_id: groupId,
		});

		client.people.set({ role });
	};

	const setOrganization = (org: models.group.Group) => {
		if (org) {
			client.register({
				organization_id: String(org.id),
				organization_name: org.name,
			});
		} else {
			client.register({
				organization_id: null,
				organization_name: '',
			});
		}
	};

	const setPerson = () => {
		// A guest who signs in is no longer one, even before a role resolves.
		if (client.get_property('role') === GuestRole) {
			client.unregister('role');
		}

		client.identify(account.trackingHash);
		client.register({ user_id: account.trackingHash });
		identified = true;
		setMembership(membership);
		setOrganization(org);
	};

	useEffect(() => {
		if (shouldNotTrack()) {
			return;
		}
		setMembership(membership);
	}, [membership, memberships]);

	useEffect(() => {
		if (shouldNotTrack()) {
			return;
		}

		setOrganization(org);
	}, [org]);

	useComponentDidMount(async () => {
		if (hasFlag(account, AccountFlags.OptOutAnalytics)) {
			optedOut = true;
			customEventQueue = [];
			rememberOptOut(true);
			return;
		}

		// Another account on this device may have opted out earlier.
		optedOut = false;
		rememberOptOut(false);

		await initClient();

		setPerson();
		startSession();
		flushQueue();
	});

	useEffect(() => {
		if (hasFlag(account, AccountFlags.OptOutAnalytics)) {
			optedOut = true;
			customEventQueue = [];
			rememberOptOut(true);
		}

		if (hasFlag(account, AccountFlags.OptOutAnalytics) && client) {
			client.reset();
			client.unregister(token());

			// The public shell may have created the client before this account
			// signed in; dropping it stops anything else from reaching Mixpanel.
			client = undefined;
			initializing = undefined;
		}
	}, [...account.flags, client]);

	return <RouteObserver />;
}

// The development section's sub-page for /organization/:id/development/
// :groupId/<page>/..., or null for any other path.
export function developmentPageFromPath(path: string): string | null {
	const pathParts = path.split('/');

	if (pathParts[1] !== 'organization' || pathParts[3] !== 'development') {
		return null;
	}

	return pathParts[5] || 'development';
}

// Navigation keeps the session alive; development pages are also tracked.
// The last path RouteObserver saw. A full page load can report the same
// route twice; a page counts once until the path changes.
let lastObservedPath = '';

function RouteObserver(): JSX.Element {
	const route = useCurrentRoute();

	useEffect(() => {
		if (route.path === lastObservedPath) {
			return;
		}

		lastObservedPath = route.path;
		touchSession();

		if (route.skipAnalytics) {
			return;
		}

		const page = developmentPageFromPath(route.path);

		if (page) {
			trackEvent(Event.DevelopmentPageViewed, {
				page,
				...trackedGroup(Number(route.path.split('/')[4])),
			});
		}
	}, [route.path]);

	return null;
}

/**
 * Mounted by the logged-out shell, so the public registration flow is
 * tracked under an anonymous id.
 */
export function PublicAnalytics(): JSX.Element {
	useComponentDidMount(async () => {
		if (isCypress()) {
			return;
		}

		if (hasRememberedOptOut()) {
			optedOut = true;
			customEventQueue = [];
			return;
		}

		await initClient();

		client.register({ role: GuestRole });
		startSession();
		flushQueue();
	});

	return null;
}

export default Analytics;
